import React, { useState } from "react";
import { ArrowUpRight, Linkedin, Twitter, Instagram, Youtube, Mail, Send, Check } from "lucide-react";
import { toast } from "sonner";
import { profile } from "../mock/mock";
import { endpoints } from "../lib/api";

const topics = [
  "Speaking engagement",
  "Advisory / Consulting",
  "Luminarc partnership",
  "Mentorship / 1:1",
  "Something else",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    org: "",
    topic: topics[0],
    message: "",
  });
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    try {
      await endpoints.submitContact(form);
      setSent(true);
      toast.success("Message sent. Drijesh will be in touch shortly.");
      setForm({ name: "", email: "", org: "", topic: topics[0], message: "" });
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <main className="bg-[var(--cream)] text-[var(--ink)]">
      {/* HERO */}
      <section className="pt-16 md:pt-24 pb-12 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="tag-mono mb-6">[ Contact ]</div>
          <h1 className="headline-xl max-w-5xl">
            Let’s build something that <span className="italic-serif text-[var(--molten)]">actually matters.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[var(--ink)]/75 max-w-2xl leading-relaxed">
            Whether it’s a keynote, an advisory engagement, or a Luminarc partnership — tell me what you’re trying to build. I reply personally.
          </p>
        </div>
      </section>

      {/* FORM + ASIDE */}
      <section className="py-12 md:py-20 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-10">
          <form onSubmit={submit} className="col-span-12 md:col-span-7 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Your name" name="name" value={form.name} onChange={onChange} placeholder="Full name" />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} placeholder="you@work.com" />
            </div>
            <Field label="Organization" name="org" value={form.org} onChange={onChange} placeholder="Company / Institution (optional)" />

            <div>
              <div className="tag-mono mb-3">What is this about?</div>
              <div className="flex flex-wrap gap-2">
                {topics.map((t) => (
                  <button
                    type="button"
                    key={t}
                    onClick={() => setForm({ ...form, topic: t })}
                    className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-[0.12em] border transition-colors ${
                      form.topic === t
                        ? "bg-[var(--ink)] text-[var(--cream)] border-[var(--ink)]"
                        : "border-[var(--ink)]/25 text-[var(--ink)]/70 hover:border-[var(--ink)]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="tag-mono mb-3">Your message</div>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                rows={6}
                placeholder="Tell me about what you are working on, what outcome you want, and any timelines..."
                className="w-full bg-transparent border-b-2 border-[var(--ink)]/25 focus:border-[var(--molten)] outline-none py-3 font-serif text-xl leading-snug resize-none transition-colors placeholder:text-[var(--ink)]/35"
              />
            </div>

            <div className="flex items-center gap-4 flex-wrap">
              <button type="submit" className="btn-ink">
                {sent ? <><Check size={16} /> Sent</> : <>Send message <Send size={15} /></>}
              </button>
              <span className="text-sm text-[var(--ink)]/55">
                Or email directly: <a className="link-underline" href={`mailto:${profile.email}`}>{profile.email}</a>
              </span>
            </div>
          </form>

          <aside className="col-span-12 md:col-span-5">
            <div className="p-8 border border-[var(--ink)]/15 bg-[var(--cream-soft)]">
              <div className="tag-mono mb-5">[ Direct channels ]</div>
              <a href={`mailto:${profile.email}`} className="group flex items-center justify-between py-4 border-b border-[var(--ink)]/15">
                <span className="flex items-center gap-3 font-serif text-xl"><Mail size={18} className="text-[var(--molten)]" /> Email</span>
                <ArrowUpRight size={18} className="text-[var(--ink)]/40 group-hover:text-[var(--molten)] transition-colors" />
              </a>
              <a href={profile.socials.linkedin} target="_blank" rel="noreferrer" className="group flex items-center justify-between py-4 border-b border-[var(--ink)]/15">
                <span className="flex items-center gap-3 font-serif text-xl"><Linkedin size={18} className="text-[var(--molten)]" /> LinkedIn</span>
                <ArrowUpRight size={18} className="text-[var(--ink)]/40 group-hover:text-[var(--molten)] transition-colors" />
              </a>
              <a href={profile.socials.twitter} target="_blank" rel="noreferrer" className="group flex items-center justify-between py-4 border-b border-[var(--ink)]/15">
                <span className="flex items-center gap-3 font-serif text-xl"><Twitter size={18} className="text-[var(--molten)]" /> X / Twitter</span>
                <ArrowUpRight size={18} className="text-[var(--ink)]/40 group-hover:text-[var(--molten)] transition-colors" />
              </a>
              <a href={profile.socials.youtube} target="_blank" rel="noreferrer" className="group flex items-center justify-between py-4 border-b border-[var(--ink)]/15">
                <span className="flex items-center gap-3 font-serif text-xl"><Youtube size={18} className="text-[var(--molten)]" /> YouTube</span>
                <ArrowUpRight size={18} className="text-[var(--ink)]/40 group-hover:text-[var(--molten)] transition-colors" />
              </a>
              <a href={profile.socials.instagram} target="_blank" rel="noreferrer" className="group flex items-center justify-between py-4">
                <span className="flex items-center gap-3 font-serif text-xl"><Instagram size={18} className="text-[var(--molten)]" /> Instagram</span>
                <ArrowUpRight size={18} className="text-[var(--ink)]/40 group-hover:text-[var(--molten)] transition-colors" />
              </a>
            </div>

            <div className="mt-6 p-8 section-ink">
              <div className="tag-mono mb-3">[ Response time ]</div>
              <p className="font-serif text-2xl text-[var(--cream)] leading-snug">
                Typically within <span className="italic-serif text-[var(--molten)]">48 hours</span>, Monday to Friday.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}

function Field({ label, name, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <div className="tag-mono mb-3">{label}</div>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-transparent border-b-2 border-[var(--ink)]/25 focus:border-[var(--molten)] outline-none py-3 font-serif text-xl transition-colors placeholder:text-[var(--ink)]/35"
      />
    </div>
  );
}
